import { useRouter } from "next/router";
import useSWR from "swr";
import { getTicketsByIds } from "~features/seated-reservation/graphql/queries";
import { TicketExchangeData } from "../types";

/**
 * Hook to manage ticket exchange data
 * Handles fetching and processing data for ticket exchanges
 */
export const useTicketExchangeData = (
  organizationId?: string
): TicketExchangeData => {
  const { query } = useRouter();

  // Check if query.ownedTickets exists and is not empty
  const hasOwnedTickets =
    typeof query?.ownedTickets === "string" ||
    Array.isArray(query?.ownedTickets);

  const { data: ownedTicketData, error: ownedTicketDataError } = useSWR(
    organizationId && hasOwnedTickets
      ? [
          "ownedTickets",
          organizationId,
          Array.isArray(query?.ownedTickets)
            ? query?.ownedTickets.join(",")
            : query?.ownedTickets,
        ]
      : null,
    getTicketsByIds
  );

  const ticketsLoading =
    !ownedTicketData && !ownedTicketDataError && hasOwnedTickets;

  // Safely check if getTicketsByIds exists and has data
  const hasTickets =
    Array.isArray(ownedTicketData?.getTicketsByIds) &&
    ownedTicketData.getTicketsByIds.length > 0;

  const ownedTicketsExchangeValue = hasTickets
    ? ownedTicketData.getTicketsByIds.reduce((acc, ticket) => {
        return acc + (ticket.lineItem?.price ?? 0);
      }, 0)
    : 0;

  return {
    ownedTicketData,
    hasOwnedTickets,
    ticketsLoading,
    hasTickets,
    ownedTicketsExchangeValue,
  };
};
